import {
  Autocomplete,
  Box,
  BoxStatus,
  GraphicFilters,
  Input,
  Label,
  PieCharts,
  Select,
  shootToast,
  Stack,
  StackRow,
} from "codekit";
import { useCallback, useEffect, useState } from "react";
import { useGetClientGraphicByIdMutation } from "../../../../libs/redux/slices/Reportxx/Repfinac/repfinacApiSlice";
import {
  useGenerateExcelOpesnfacMutation,
  useGetDataGraphicOpesnfacMutation,
  useGetDataGroupsOpesnfacMutation,
} from "../../../../libs/redux/slices/Graficsx/Opesnfac/opesnfacApiSlice";
import { Controller, useForm, useWatch } from "react-hook-form";
import { convertCurrencyNumber, getActualDate } from "../../../../utils/globalFunctions";

const colorChart = ["secondary", "success", "warning", "error", "accent", "primary", "info", "neutral"];

const Opesnfac = ({ graphName }) => {
  const [getClientById] = useGetClientGraphicByIdMutation();
  const [resultsClient, setResultsClient] = useState([]);
  const [getDataGraphic] = useGetDataGraphicOpesnfacMutation();
  const [getDataGroups] = useGetDataGroupsOpesnfacMutation();
  const [generateExcel] = useGenerateExcelOpesnfacMutation();
  const [total, setTotal] = useState(0);
  const [dataGraphic, setDataGraphic] = useState([]);
  const [colorsChart, setColorsChart] = useState([]);
  const firstDate = `${getActualDate().substring(0, 8)}01`;
  const { control, register, reset, setValue } = useForm({
    mode: "onBlur",
    defaultValues: { REGFECIN: firstDate, REGFECFN: getActualDate(), CCOIDXXX: "", CLIIDXXX: "" },
  });
  const values = useWatch({ control });
  const [isGraphicGroups, setGraphicGroups] = useState(false);

  const searchClient = async (CLIIDXXX, cb) => {
    return await getClientById({
      filters: { CLIIDXXX },
    })
      .unwrap()
      .then((response) => {
        setResultsClient(response.data);
      })
      .catch((err) => {
        console.log("entro en el catch", err);
      })
      .finally(() => {
        cb();
      });
  };

  //Funcion que retorna los filtros aplicados a la grafica
  const getFilters = useCallback((values) => {
    let filters = { "MDOSYYYY.REGFECXX": { BETWEEN: `${values.REGFECIN},${values.REGFECFN}` } };
    if (values.CLIIDXXX) {
      filters = { ...filters, "MDOSYYYY.CLIIDXXX": values.CLIIDXXX ? values.CLIIDXXX.split(" - ")[0] : "" };
    }
    if (values.CCOIDXXX) {
      filters = { ...filters, "MDOSYYYY.CCOIDXXX": values.CCOIDXXX };
    }
    if (values.DOCTIPXX) {
      filters = { ...filters, "MDOSYYYY.DOCTIPXX": values.DOCTIPXX };
    }
    return filters;
  }, []);

  //Funcion para setear la data de la grafica
  const setDataGraphics = (data) => {
    let totalData = 0;
    let percentage = {};
    data.forEach((element) => {
      totalData += parseFloat(element.CANTIDAD);
    });
    data.forEach((element) => {
      const value = (parseFloat(element.CANTIDAD) * 100) / totalData;
      percentage = {
        ...percentage,
        [element.CCOIDXXX]: value.toFixed(2),
      };
    });
    setDataGraphic(
      data.map((item) => ({
        name: item.CCODESXX,
        value: parseFloat(item.CANTIDAD),
        date: `${percentage[item.CCOIDXXX]} %`,
        CCOIDXXX: item.CCOIDXXX,
        GRUPIDXX: item.GRUPIDXX ?? "",
      }))
    );
    setColorsChart(
      data.map((item, index) => ({
        colorCellChart: `${colorChart[index]}-500`,
        bgColorCards: `${colorChart[index]}-100`,
        iconColor: `${colorChart[index]}-400`,
        textColor: `${colorChart[index]}-600`,
      }))
    );
    setTotal(totalData);
  };

  //Funcion para la consulta de informacion de la Graphica
  const handleGetData = useCallback(
    async (values, type = "GERENCIA") => {
      if (type === "GERENCIA") {
        await getDataGraphic({ filters: getFilters(values) })
          .unwrap()
          .then((response) => {
            const { data } = response;
            setDataGraphics(data);
          })
          .catch();
      } else {
        await getDataGroups({ filters: getFilters(values) })
          .unwrap()
          .then((response) => {
            const { data } = response;
            setDataGraphics(data);
          })
          .catch();
      }
    },
    [getDataGraphic, getDataGroups, getFilters]
  );

  //Funcion para la generacion del excel
  const handleGenerateExcel = async (context) => {
    let filters = { ...values };
    if (context) {
      const {
        data: { CCOIDXXX, GRUPIDXX },
      } = context;
      filters = { ...filters, CCOIDXXX, GRUPIDXX };
    }
    await generateExcel({ filters: getFilters(filters) })
      .unwrap()
      .then(() => {
        shootToast({
          variant: "success",
          text: "Tu reporte se esta generando! \n En Pocos minutos recibiras las confirmación del mismo por correo electronico",
          duration: 5000,
        });
      })
      .catch();
  };

  //Hook para volver a realizar consulta cuando cambia algun filtro
  useEffect(() => {
    handleGetData(values, isGraphicGroups ? "GRUPOS" : "GERENCIA");
  }, [handleGetData, values, isGraphicGroups]);

  return (
    <>
      <Box spacing={4}>
        <GraphicFilters
          containerName={graphName}
          graphicKeyName="ingeprop"
          clearFilters={() => {
            reset();
            setGraphicGroups(false);
          }}
          onClickVerMas={() => {
            handleGenerateExcel();
          }}
        >
          <Box>
            <Box mb={8} borderBottom={1} spacing={2}>
              <Stack gap={3} columnsVariant="form-3">
                <StackRow>
                  <Label>Cliente</Label>
                  <Controller
                    control={control}
                    name={`CLIIDXXX`}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Autocomplete
                        placeholder="Buscar..."
                        textLoading="Buscando..."
                        textNoResults="No se encontraron registros"
                        minLengthToSearch={2}
                        selected={value}
                        searchResults={resultsClient}
                        onChangeSearch={(value, cb) => searchClient(value, cb)}
                        onItemClick={(context) => {
                          onChange(`${context.item.label}`);
                        }}
                        onResetSelected={() => {
                          onChange("");
                        }}
                      />
                    )}
                  />
                </StackRow>
                <StackRow>
                  <Label>Tipo de Operación</Label>
                  <Controller
                    control={control}
                    name={`DOCTIPXX`}
                    rules={{ required: true }}
                    render={({ field: { onChange, value } }) => (
                      <Select
                        value={value}
                        options={[
                          { text: "SELECCIONE", value: "" },
                          { text: "IMPORTACION", value: "IMPORTACION" },
                          { text: "EXPORTACION", value: "EXPORTACION" },
                          { text: "REGISTRO", value: "REGISTRO" },
                          { text: "TRANSITO", value: "TRANSITO" },
                          { text: "OTROS", value: "OTROS" },
                        ]}
                        onChangeValue={(context) => {
                          onChange(context.option.value);
                        }}
                      />
                    )}
                  />
                </StackRow>
                <StackRow>
                  <Label>Fecha de Cración Desde</Label>
                  <Input type="date" {...register("REGFECIN")} />
                </StackRow>
                <StackRow>
                  <Label>Fecha de Cración Hasta</Label>
                  <Input type="date" {...register("REGFECFN")} />
                </StackRow>
              </Stack>
            </Box>
            <Box onclick={() => console.log("click")}>
              {dataGraphic.length > 0 ? (
                <Stack gap={8}>
                  <StackRow>
                    <PieCharts
                      onClickCellChart={(context) => {
                        setGraphicGroups(true);
                        setValue("CCOIDXXX", context?.data?.CCOIDXXX);
                      }}
                      onClickIconCard={(context) => {
                        handleGenerateExcel(context);
                      }}
                      onClickCard={(context) => {
                        setGraphicGroups(true);
                        setValue("CCOIDXXX", context?.data?.CCOIDXXX);
                      }}
                      data={dataGraphic}
                      chartColors={colorsChart}
                      labelOne={convertCurrencyNumber(total)}
                      labelTwo="Total"
                      titleIcon="Generar Excel"
                    />
                  </StackRow>
                </Stack>
              ) : (
                <BoxStatus variant="empty" text="Esta sección está vacía" />
              )}
            </Box>
          </Box>
        </GraphicFilters>
      </Box>
    </>
  );
};

export default Opesnfac;
